import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Moon, Sun, Lock, LockOpen, Timer, ArrowUpRight, User } from "lucide-react";
import { blog as allPosts } from "../data/content";
import { NewsletterStrip } from "../components/blog/NewsletterStrip";
import { KofiButton }      from "../components/blog/KofiWidget";
import { WelcomePopup }    from "../components/blog/WelcomePopup";
import { SupportPopup }    from "../components/blog/SupportPopup";
import { useAuth } from "../auth/AuthContext";
import AuthModal from "../auth/AuthModal";
import AccountManager from "../auth/AccountManager";

const BLOG_THEME_KEY = "mc_blog_theme";

const FILTERS = [
  { key: "All", label: "All" },
  { key: "Engineering", label: "Engineering" },
  { key: "Finance", label: "Finance" },
  { key: "Life", label: "Life" }
];

const IMG_BG_FALLBACKS = [
  "var(--blog-bg-img-1)",
  "var(--blog-bg-img-2)",
  "var(--blog-bg-img-3)",
  "var(--blog-bg-img-4)",
  "var(--blog-bg-img-5)",
  "var(--blog-bg-img-6)"
];

function parseDateISO(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getAccess(post) {
  const raw = post.access || (
    post.lockMode === "unlocked" ? "open" :
    post.lockMode === "locked"   ? "members" :
    post.lockMode === "timer"    ? "scheduled" : "open"
  );
  if (raw === "scheduled" && post.unlockAt) {
    if (Date.now() >= new Date(post.unlockAt).getTime()) return "open";
  }
  return raw;
}

function AccessBadge({ access, scheduledDate }) {
  if (access === "open") {
    return (
      <span
        data-testid="badge-open"
        className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono text-[9px] tracking-[0.06em]"
        style={{
          background: "var(--blog-badge-open-bg)",
          color: "var(--blog-badge-open-color)",
          border: "0.5px solid var(--blog-badge-open-border)"
        }}
      >
        <LockOpen className="w-2.5 h-2.5" />
        Open
      </span>
    );
  }
  if (access === "members") {
    return (
      <span
        data-testid="badge-members"
        className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono text-[9px] tracking-[0.06em]"
        style={{
          background: "var(--blog-badge-locked-bg)",
          color: "var(--blog-badge-locked-color)",
          border: "0.5px solid var(--blog-badge-locked-border)"
        }}
      >
        <Lock className="w-2.5 h-2.5" />
        Members
      </span>
    );
  }
  // scheduled
  let label = "Soon";
  if (scheduledDate) {
    try {
      const d = new Date(scheduledDate);
      label = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    } catch (_) { /* noop */ }
  }
  return (
    <span
      data-testid="badge-scheduled"
      className="inline-flex items-center gap-1 rounded-full px-2 py-[2px] font-mono text-[9px] tracking-[0.06em]"
      style={{
        background: "var(--blog-badge-timer-bg)",
        color: "var(--blog-badge-timer-color)",
        border: "0.5px solid var(--blog-badge-timer-border)"
      }}
    >
      <Timer className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

function AccentDash({ access }) {
  const color = access === "members"
    ? "var(--blog-accent-magenta)"
    : access === "scheduled"
      ? "var(--blog-accent-amber)"
      : "var(--blog-accent-teal)";
  return <span className="inline-block" style={{ width: 18, height: "1.5px", background: color, flexShrink: 0 }} />;
}

function PostCard({ post, index }) {
  const access = getAccess(post);
  const imageBg = post.imageBg || IMG_BG_FALLBACKS[index % IMG_BG_FALLBACKS.length];

  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-post-card-${post.slug}`}
      className="group block transition-colors"
      style={{ borderRight: "0.5px solid var(--blog-border-tertiary)" }}
    >
      <div
        className="post-card-inner h-full"
        style={{ transition: "background 150ms ease" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "var(--blog-bg-secondary)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      >
        <div
          className="w-full overflow-hidden"
          style={{ aspectRatio: "4/3", background: imageBg, position: "relative" }}
        >
          {post.coverImage && (
            <img
              src={post.coverImage}
              alt={post.title}
              loading={index < 3 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "auto"}
              decoding={index < 3 ? "sync" : "async"}
              style={{
                position: "absolute", inset: 0,
                width: "100%", height: "100%",
                objectFit: "cover",
                filter: "contrast(1.1) brightness(0.78) saturate(0.80)"
              }}
            />
          )}
        </div>
        <div className="px-[18px] pt-4 pb-[18px]">
          <div className="flex items-center gap-2 mb-2">
            <AccentDash access={access} />
            <span
              className="font-mono uppercase"
              style={{ fontSize: 9, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}
            >
              {post.category || post.tags?.[0] || "Writing"}
            </span>
            <span className="ml-auto">
              <AccessBadge access={access} scheduledDate={post.scheduledDate} />
            </span>
          </div>
          <h3
            className="font-display font-medium leading-snug mb-2"
            style={{ fontSize: 14, color: "var(--blog-text-primary)" }}
          >
            {post.title}
          </h3>
          <p
            className="mb-3"
            style={{ fontSize: 11.5, lineHeight: 1.55, color: "var(--blog-text-secondary)" }}
          >
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between">
            <span style={{ fontSize: 10, color: "var(--blog-text-tertiary)" }}>{post.date}</span>
            <span style={{ fontSize: 10, color: "var(--blog-text-tertiary)" }}>{post.readTime}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function FeaturedCard({ post }) {
  if (!post) return null;
  const access = getAccess(post);
  const imageBg = post.imageBg || "var(--blog-featured-img-bg)";
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid="blog-featured-card"
      className="flex group"
      style={{
        borderRight: "0.5px solid var(--blog-border-tertiary)",
        transition: "background 150ms ease"
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--blog-bg-secondary)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
    >
      <div
        className="hidden sm:block overflow-hidden"
        style={{ width: 180, flexShrink: 0, minHeight: 180, background: imageBg, position: "relative" }}
      >
        {post.coverImage && (
          <img
            src={post.coverImage}
            alt={post.title}
            loading="eager"
            fetchPriority="high"
            decoding="sync"
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover",
              filter: "contrast(1.1) brightness(0.78) saturate(0.80)"
            }}
          />
        )}
      </div>
      <div className="flex-1 p-[22px] flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <AccentDash access={access} />
            <span
              className="font-mono uppercase"
              style={{ fontSize: 9, letterSpacing: "0.12em", color: "var(--blog-text-tertiary)" }}
            >
              Featured · {post.category || post.tags?.[0]}
            </span>
            <span className="ml-auto">
              <AccessBadge access={access} scheduledDate={post.scheduledDate} />
            </span>
          </div>
          <h3
            className="font-display font-medium mb-[10px] leading-snug"
            style={{ fontSize: 18, color: "var(--blog-text-primary)" }}
          >
            {post.title}
          </h3>
          <p style={{ fontSize: 12.5, lineHeight: 1.6, color: "var(--blog-text-secondary)" }}>
            {post.excerpt}
          </p>
        </div>
        <div
          className="mt-4 flex items-center gap-[10px]"
          style={{ fontSize: 10, color: "var(--blog-text-tertiary)" }}
        >
          <span>{post.date}</span>
          <span>·</span>
          <span>{post.readTime} read</span>
          <ArrowRight className="ml-auto w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

function Sidebar({ recent }) {
  return (
    <div className="p-5 flex flex-col" data-testid="blog-sidebar-recent">
      <div
        className="pb-3 mb-1 font-mono uppercase"
        style={{
          fontSize: 10,
          letterSpacing: "0.10em",
          color: "var(--blog-text-tertiary)",
          borderBottom: "0.5px solid var(--blog-border-tertiary)"
        }}
      >
        Recent Posts
      </div>
      {recent.map((post, i) => (
        <Link
          key={post.slug}
          to={`/blog/${post.slug}`}
          data-testid={`blog-sidebar-item-${post.slug}`}
          className="block py-3 group"
          style={{
            borderBottom: i === recent.length - 1 ? "none" : "0.5px solid var(--blog-border-tertiary)"
          }}
        >
          <div
            className="font-display font-medium mb-1 leading-snug transition-colors"
            style={{ fontSize: 11.5, color: "var(--blog-text-primary)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--blog-accent-teal)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--blog-text-primary)"; }}
          >
            {post.title}
          </div>
          <div style={{ fontSize: 10, color: "var(--blog-text-tertiary)" }}>
            {post.date} · {post.readTime}
          </div>
        </Link>
      ))}
    </div>
  );
}

function BackToTop() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const fn = () => setShow(window.scrollY > 400);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  if (!show) return null;
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      style={{
        position: "fixed", bottom: 28, right: 28, zIndex: 999,
        width: 44, height: 44, borderRadius: "50%",
        background: "#0E3A3A", color: "#fff", border: "none",
        cursor: "pointer", fontSize: 18, lineHeight: 1,
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      }}
    >↑</button>
  );
}

export default function BlogListPage() {
  const { user } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(BLOG_THEME_KEY) || "light";
  });
  const [activeFilter, setActiveFilter] = useState("All");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.classList.remove("dark");
    document.title = "The Journal With Malicke";
    return () => {
      document.documentElement.classList.add("dark");
      document.title = "Malicke Case";
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem(BLOG_THEME_KEY, theme); } catch (_) { /* noop */ }
  }, [theme]);

  const sorted = useMemo(() => {
    return allPosts
      .slice()
      .sort((a, b) => parseDateISO(b.dateISO) - parseDateISO(a.dateISO));
  }, []);

  const filtered = useMemo(() => {
    if (activeFilter === "All") return sorted;
    return sorted.filter((p) => (p.category || "") === activeFilter);
  }, [activeFilter, sorted]);

  const gridPosts = filtered.slice(0, 3);
  const featuredCandidate = filtered.find((p) => p.featured) || filtered[3] || filtered[0];
  const featured = featuredCandidate && !gridPosts.some(p => p.slug === featuredCandidate.slug)
    ? featuredCandidate
    : filtered[3] || null;
  const recent = filtered
    .filter((p) => p.slug !== featured?.slug && !gridPosts.some(g => g.slug === p.slug))
    .slice(0, 3);
  const fallbackRecent = recent.length > 0 ? recent : sorted.filter(p => p.slug !== featured?.slug).slice(0, 3);

  // Promoted article banner (v2 design)  -  renders only when an editor explicitly
  // sets `promoted: true` in the post's frontmatter. Stays hidden otherwise.
  const promoted = sorted.find((p) => p.promoted && getAccess(p) === "open");

  const isDark = theme === "dark";
  const pageBg = isDark ? "#050506" : "#E6E2DA";

  return (
    <div
      className="blog-theme min-h-screen"
      data-blog-theme={isDark ? "dark" : "light"}
      data-testid="blog-list-page"
      style={{
        background: pageBg,
        fontFamily: "'Inter', system-ui, sans-serif",
        transition: "background 200ms ease",
        padding: "32px 16px 64px"
      }}
    >
      {/* ── Top controls ── */}
      <div className="mx-auto flex justify-end gap-[10px] mb-1" style={{ width: "100%", maxWidth: 980 }}>
        <button
          data-testid="blog-theme-toggle"
          onClick={() => setTheme(isDark ? "light" : "dark")}
          className="inline-flex items-center gap-2 rounded-full px-[14px] py-[6px]"
          style={{
            fontSize: 11,
            letterSpacing: "0.06em",
            border: "1px solid var(--blog-border-primary)",
            background: "var(--blog-bg-primary)",
            color: "var(--blog-text-secondary)",
            cursor: "pointer"
          }}
        >
          {isDark ? <Sun className="w-3 h-3" /> : <Moon className="w-3 h-3" />}
          {isDark ? "Light mode" : "Dark mode"}
        </button>
      </div>

      {/* ── Blog shell ── */}
      <div
        className="mx-auto overflow-hidden"
        style={{
          width: "100%",
          maxWidth: 980,
          background: "var(--blog-bg-primary)",
          borderRadius: 12,
          border: "0.5px solid var(--blog-border-primary)",
          transition: "background 200ms ease, border-color 200ms ease"
        }}
      >
        {/* Nav */}
        <nav
          className="px-7 pt-[14px] pb-[14px]"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          {/* Row 1: brand + account  -  always visible */}
          <div className="flex items-center justify-between">
            <Link
              to="/"
              data-testid="blog-nav-logo"
              className="font-display font-medium uppercase tracking-[0.12em]"
              style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}
            >
              Malicke Case
            </Link>
            {/* Desktop center links */}
            <div className="hidden sm:flex gap-6">
              <Link to="/blog/about" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>About</Link>
              <span className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-primary)" }}>Journal</span>
              <Link to="/blog/contact" className="uppercase" style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
            </div>
  {user ? (
              <AccountManager />
            ) : (
            <button
              data-testid="blog-nav-login"
              onClick={() => setAuthOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-full"
              style={{
                fontSize: 11,
                letterSpacing: "0.04em",
                padding: "6px 14px",
                border: "0.5px solid var(--blog-border-primary)",
                background: "var(--blog-bg-secondary)",
                color: "var(--blog-text-primary)",
                cursor: "pointer",
                fontFamily: "inherit"
              }}
            >
              <User className="w-3 h-3" />
              Sign in
            </button>
          )}
          </div>
          {/* Mobile-only second row: nav links centered */}
          <div className="sm:hidden flex items-center justify-center gap-5 pt-3">
            <Link to="/blog/about" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>About</Link>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <span className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-primary)" }}>Journal</span>
            <span style={{ color: "var(--blog-border-secondary)", fontSize: 10 }}>·</span>
            <Link to="/blog/contact" className="uppercase" style={{ fontSize: 10, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>Contact</Link>
          </div>
        </nav>

        {/* Hero */}
        <div
          className="px-7 pt-10 pb-7 flex flex-wrap items-end justify-between gap-6"
          style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
        >
          <div>
            <div
              className="uppercase mb-2"
              style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
            >
              Writing &amp; Ideas
            </div>
            <h1
              className="font-display font-medium"
              style={{
                fontSize: "clamp(36px, 6vw, 52px)",
                lineHeight: 1,
                letterSpacing: "-0.02em",
                color: "var(--blog-text-primary)"
              }}
            >
              The<br />
              <span style={{ color: "var(--blog-accent-teal)" }}>Journal</span>
            </h1>
            <p
              className="mt-3"
              style={{ fontSize: 12, lineHeight: 1.6, color: "var(--blog-text-tertiary)", maxWidth: 280 }}
            >
              Finance, Technology, engineering and lifestyle. Written by Malicke Case.
            </p>
          </div>
          <div className="flex flex-col items-end gap-[10px] shrink-0">
            <div
              data-testid="blog-post-count"
              style={{ fontSize: 11, letterSpacing: "0.06em", color: "var(--blog-text-tertiary)" }}
            >
              {sorted.length} {sorted.length === 1 ? "article" : "articles"}
            </div>
            <div className="flex flex-wrap gap-[6px]">
              {FILTERS.map((f) => {
                const isActive = activeFilter === f.key;
                return (
                  <button
                    key={f.key}
                    data-testid={`blog-filter-${f.key.toLowerCase()}`}
                    onClick={() => setActiveFilter(f.key)}
                    className="rounded-full px-3 py-[4px] transition-colors"
                    style={{
                      fontSize: 10,
                      letterSpacing: "0.04em",
                      cursor: "pointer",
                      border: `0.5px solid ${isActive ? "var(--blog-text-primary)" : "var(--blog-border-secondary)"}`,
                      background: isActive ? "var(--blog-text-primary)" : "transparent",
                      color: isActive ? "var(--blog-bg-primary)" : "var(--blog-text-secondary)"
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* 3-column card grid */}
        {gridPosts.length > 0 ? (
          <div
            className="grid grid-cols-1 sm:grid-cols-3"
            style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
          >
            {gridPosts.map((post, i) => (
              <PostCard key={post.slug} post={post} index={i} />
            ))}
          </div>
        ) : (
          <div className="py-16 text-center" style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)", color: "var(--blog-text-tertiary)" }}>
            No posts under this filter yet.
          </div>
        )}

        {/* Promoted banner  -  copper card with quirky chart graphic */}
        {promoted && (
          <div className="jfc-wrap" data-testid="blog-promoted-banner">
            <div className="jfc-left">
              <div>
                <p className="jfc-label">Featured</p>
                  {/* Title is editor-controlled frontmatter  -  splitting on word boundary only, no user input */}
                <h3
                  className="jfc-title"
                  dangerouslySetInnerHTML={{
                    __html: (promoted.title || "")
                      .split(/\s(?:and|&)\s/i)
                      .slice(0, 2)
                      .join("<br/>&amp; ")
                  }}
                />
              </div>
              <Link
                to={`/blog/${promoted.slug}`}
                className="jfc-btn"
                data-testid="blog-promoted-cta"
              >
                Read more →
              </Link>
            </div>
            <div className="jfc-right">
              <svg viewBox="0 0 560 130" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect width="560" height="130" fill="var(--blog-bg-tertiary)" />
                <g opacity=".14" stroke="#00C2A8" strokeWidth="1">
                  <path d="M0 90 Q140 74 280 90 Q420 106 560 90" />
                  <path d="M0 72 Q100 52 220 68 Q380 88 560 68" />
                  <path d="M0 108 Q160 98 300 110 Q440 120 560 108" />
                  <path d="M0 55 Q80 35 180 50 Q340 72 560 46" />
                  <path d="M0 124 Q180 118 320 126 Q470 132 560 124" />
                </g>
                <g>
                  <circle cx="80"  cy="65" r="18" stroke="#00C2A8" strokeWidth="1.3" fill="none" />
                  <circle cx="80"  cy="65" r="4"  fill="#00C2A8" opacity=".5" />
                  <circle cx="180" cy="45" r="11" stroke="#00C2A8" strokeWidth="1.2" fill="none" />
                  <circle cx="180" cy="45" r="3"  fill="#00C2A8" opacity=".5" />
                  <circle cx="280" cy="72" r="13" stroke="#C9A24A" strokeWidth="1.2" fill="none" />
                  <circle cx="280" cy="72" r="3.5" fill="#C9A24A" opacity=".5" />
                  <circle cx="360" cy="40" r="10" stroke="#00C2A8" strokeWidth="1.2" fill="none" />
                  <circle cx="360" cy="40" r="3"  fill="#00C2A8" opacity=".5" />
                  <circle cx="380" cy="95" r="11" stroke="#C9A24A" strokeWidth="1.2" fill="none" />
                  <circle cx="380" cy="95" r="3"  fill="#C9A24A" opacity=".5" />
                  <circle cx="460" cy="55" r="9"  stroke="#00C2A8" strokeWidth="1.2" fill="none" />
                  <circle cx="460" cy="55" r="2.5" fill="#00C2A8" opacity=".5" />
                  <circle cx="500" cy="88" r="7"  stroke="#C9A24A" strokeWidth="1.2" fill="none" />
                  <circle cx="500" cy="88" r="2"  fill="#C9A24A" opacity=".5" />
                  <circle cx="536" cy="58" r="5"  stroke="#00C2A8" strokeWidth="1" fill="none" />
                </g>
              </svg>
            </div>
          </div>
        )}

        {/* Featured + Sidebar */}
        {(featured || fallbackRecent.length > 0) && (
          <div
            className="grid grid-cols-1 sm:grid-cols-[2fr_1fr]"
            style={{ borderBottom: "0.5px solid var(--blog-border-tertiary)" }}
          >
            <FeaturedCard post={featured} />
            <Sidebar recent={fallbackRecent} />
          </div>
        )}

        {/* Newsletter */}
        <NewsletterStrip />

        {/* Pagination (static  -  Phase 1) */}
        <div className="flex items-center justify-between px-7 py-4">
          <span style={{ fontSize: 11, color: "var(--blog-text-tertiary)" }}>
            Showing {filtered.length} of {sorted.length} articles
          </span>
          <div className="flex gap-1">
            <span
              className="inline-flex items-center justify-center rounded"
              style={{
                width: 28,
                height: 28,
                fontSize: 11,
                background: "var(--blog-text-primary)",
                color: "var(--blog-bg-primary)",
                border: "0.5px solid var(--blog-text-primary)"
              }}
            >
              1
            </span>
          </div>
        </div>
      </div>

      {/* Bottom: also a "more from" hint */}
      <div className="mx-auto mt-6 flex items-center justify-between gap-2 flex-wrap" style={{ width: "100%", maxWidth: 980 }}>
        <a
          href="/feed.xml"
          data-testid="blog-rss-link"
          className="font-mono uppercase inline-flex items-center gap-1"
          style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}
        >
          RSS feed
          <ArrowUpRight className="w-3 h-3" />
        </a>
      </div>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode={authMode} />

      {/* Ko-fi floating button  -  visible on all blog pages */}
      <KofiButton isDark={isDark} />

      {/* Back to top */}
      <BackToTop />

      {/* Welcome popup  -  fires once per 7 days for guests */}
      <WelcomePopup
        isDark={isDark}
        onOpenSignup={() => { setAuthMode("signup"); setAuthOpen(true); }}
        onOpenNewsletter={() => { setAuthMode("signup"); setAuthOpen(true); }}
      />

      {/* Support popup  -  fires once per session after 30s */}
      <SupportPopup isDark={isDark} />
    </div>
  );
}
